import { ChipIcon, CodeIcon } from '@heroicons/react/outline';
import Trans from 'next-translate/Trans';
import useLocale from 'next-translate/useTranslation';
import {
  FaFacebookMessenger,
  FaPhone,
  FaSlack,
  FaLine,
  FaTelegram,
  FaWhatsapp,
  FaWordpress,
} from 'react-icons/fa6';

import ZapierSVG from '~/svg/Zapier.svg';

export default function AgentChannels() {
  const { t } = useLocale('chatbots');

  const more = [
    {
      name: 'Zapier',
      logo: <ZapierSVG className='h-6 w-auto fill-[#FF4A00]' />,
    },
    {
      name: 'WordPress',
      logo: <FaWordpress className='h-6 w-6 text-[#21759B]' />,
    },
    {
      name: 'Google Calendar',
      logo: (
        <img
          src='/images/integrations/google-calendar.svg'
          alt='Google Calendar'
          className='h-6 w-auto'
        />
      ),
    },
    {
      name: t('HTML Embed'),
      logo: <CodeIcon className='h-6 w-6 text-primary-600' />,
    },
    { name: 'API', logo: <ChipIcon className='h-6 w-6 text-primary-600' /> },
  ];

  return (
    <section
      id='channels'
      className='bg-gray-50 py-24 dark:bg-slate-800 sm:py-32'
    >
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='max-w-3xl'>
          <h2 className='text-sm font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400'>
            {t('Every channel')}
          </h2>
          <p className='mt-4 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
            <Trans
              i18nKey='channels-headline'
              ns='chatbots'
              components={[
                <span
                  key='0'
                  className='font-serif font-medium italic text-primary-600 dark:text-primary-400'
                />,
              ]}
            />
          </p>
        </div>

        <div className='mt-16 grid grid-cols-1 gap-6 lg:grid-cols-3'>
          {/* Website */}
          <div className='rounded-3xl bg-white p-8 ring-1 ring-gray-900/5 dark:bg-slate-900/60 dark:ring-white/10'>
            <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-600/10'>
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.8'
                className='h-6 w-6 text-primary-600 dark:text-primary-400'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z'
                />
              </svg>
            </div>
            <h3 className='mt-6 text-xl font-bold text-gray-900 dark:text-white'>
              {t('Chat on your website')}
            </h3>
            <p className='mt-3 text-sm leading-relaxed text-gray-600 dark:text-slate-400'>
              {t(
                'A branded chat widget, embedded with one line of code — or share your agent via public link.',
              )}
            </p>
            <div className='mt-6 rounded-xl bg-gray-900 px-4 py-3 font-mono text-xs leading-relaxed text-gray-300'>
              <span className='text-gray-500'>&lt;</span>
              <span className='text-sky-400'>script </span>
              <span className='text-emerald-400'>src</span>
              <span className='text-gray-500'>=</span>
              <span className='text-amber-300'>"chat.min.js"</span>
              <span className='text-gray-500'>/&gt;</span>
            </div>
          </div>

          {/* Phone — the new thing, visually loudest */}
          <div className='relative overflow-hidden rounded-3xl bg-gray-900 p-8 ring-1 ring-white/10'>
            <span className='absolute right-6 top-6 rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-400 ring-1 ring-emerald-400/30'>
              {t('New')}
            </span>
            <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10'>
              <FaPhone className='h-5 w-5 text-white' />
            </div>
            <h3 className='mt-6 text-xl font-bold text-white'>
              {t('Phone calls')}
            </h3>
            <p className='mt-3 text-sm leading-relaxed text-gray-400'>
              {t(
                'Connect a Twilio number and customers can call your agent — it listens, speaks naturally, and can be interrupted mid-sentence, like a real conversation.',
              )}
            </p>
            <div className='mt-6 flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3 ring-1 ring-white/10'>
              <span className='relative flex h-2.5 w-2.5'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60'></span>
                <span className='relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400'></span>
              </span>
              <span className='text-sm font-medium text-white'>
                {t('Answering calls 24/7')}
              </span>
            </div>
          </div>

          {/* Messaging */}
          <div className='rounded-3xl bg-white p-8 ring-1 ring-gray-900/5 dark:bg-slate-900/60 dark:ring-white/10'>
            <div className='flex items-center gap-3'>
              <span className='flex h-12 w-12 items-center justify-center rounded-2xl bg-[#25D366]/10'>
                <FaWhatsapp className='h-6 w-6 text-[#25D366]' />
              </span>
              <span className='flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0084FF]/10'>
                <FaFacebookMessenger className='h-6 w-6 text-[#0084FF]' />
              </span>
              <span className='flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0088cc]/10'>
                <FaTelegram className='h-6 w-6 text-[#0088cc]' />
              </span>
              <span className='flex h-12 w-12 items-center justify-center rounded-2xl bg-[#06C755]/10'>
                <FaLine className='h-6 w-6 text-[#06C755]' />
              </span>
              <span className='flex h-12 w-12 items-center justify-center rounded-2xl bg-[#4A154B]/10'>
                <FaSlack className='h-6 w-6 text-[#4A154B] dark:text-white' />
              </span>
            </div>
            <h3 className='mt-6 text-xl font-bold text-gray-900 dark:text-white'>
              {t('Messaging apps')}
            </h3>
            <p className='mt-3 text-sm leading-relaxed text-gray-600 dark:text-slate-400'>
              {t(
                'Meet customers where they already are: WhatsApp, Messenger, Telegram, LINE, and Slack — one agent, consistent answers.',
              )}
            </p>
          </div>
        </div>

        <div className='mt-10 flex flex-wrap items-center gap-x-8 gap-y-4'>
          <span className='text-sm font-medium text-gray-500 dark:text-slate-400'>
            {t('Also connects with')}
          </span>
          {more.map((item) => (
            <span
              key={item.name}
              className='flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-slate-300'
            >
              {item.logo}
              {item.name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
